import {
  CatalogDTO,
  transformBaseOrder,
  transformPolicy,
} from '@/common/dto/catalog';
import { EmployeeDTO } from '@/common/integrations/menhir/employee/employee/employee.dto';
import { transformEstablishment } from '@/common/integrations/menhir/employee/establishments/establishments.dto';
import { transformAppleOrderItem } from '@/common/integrations/menhir/employee/marketplace/marketplace.dto';
import {
  IDonationOrder,
  IMenhirDonationOrderEnvelope,
  IMenhirOrder,
  IMenhirOrderOperation,
  IMenhirReimbursementOrderDetails,
  IOrder,
  IOrderOperation,
  IOrderWithPolicy,
  IReimbursementOrderDetails,
  TGenericPendingOrder,
  TMenhirGenericPendingOrder,
  TMenhirRetirementInvestmentPendingOrder,
  TOrderCategorySlug,
  TRetirementInvestmentPendingOrder,
  isAppleOrder,
  isOrderWithPolicy,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import { IAppleOrder } from './orders.types';

export const transformOrder = <T extends TOrderCategorySlug>(
  order: IMenhirOrder<T>
): IOrder => {
  const baseOrder = transformBaseOrder<T>(order);
  if (isAppleOrder(order)) {
    return {
      ...baseOrder,
      marketplaceOrder: {
        id: order.marketplace_order.id,
        total: order.marketplace_order.total,
        shippingAddress: EmployeeDTO.transformAddress(
          order.marketplace_order.shipping_address
        ),
        items: order.marketplace_order.items.map(transformAppleOrderItem),
      },
      statusDetails: {
        at: order.status_details?.at,
        reason: order.status_details?.reason,
      },
      priceAdjustmentAmount: order.price_adjustment_amount,
    } as IAppleOrder;
  }
  if (isOrderWithPolicy(order)) {
    return {
      ...baseOrder,
      policy: order.policy ? transformPolicy(order.policy) : null,
    } as IOrderWithPolicy<T>;
  }
  return baseOrder;
};

export const transformRetirementInvestmentPendingOrder = (
  menhirPendingOrder: TMenhirRetirementInvestmentPendingOrder
): TRetirementInvestmentPendingOrder => {
  return {
    id: menhirPendingOrder.id,
    amount: menhirPendingOrder.amount,
    insertedAt: menhirPendingOrder.inserted_at,
    reference: menhirPendingOrder.reference,
    status: menhirPendingOrder.status,
    type: menhirPendingOrder.type,
    productName: menhirPendingOrder.product_name,
  };
};

export const transformGenericPendingOrder = (
  menhirPendingOrder: TMenhirGenericPendingOrder
): TGenericPendingOrder => {
  return {
    amount: menhirPendingOrder.amount,
    id: menhirPendingOrder.id,
    insertedAt: menhirPendingOrder.inserted_at,
    reference: menhirPendingOrder.reference,
    status: menhirPendingOrder.status,
    type: menhirPendingOrder.type,
    category: menhirPendingOrder.category,
  };
};

export const transformOrdersList = <
  T extends TOrderCategorySlug,
  U extends IOrder<T>,
>(
  orders: IMenhirOrder<T>[]
) => orders.map(transformOrder) as U[];

export const transformOrderOperation = (
  operation: IMenhirOrderOperation
): IOrderOperation => ({
  id: operation.id,
  type: operation.operation,
  status: operation.status,
});

export const transformDonationOrderDetails = ({
  order,
}: IMenhirDonationOrderEnvelope): IDonationOrder => {
  return {
    ...CatalogDTO.transformBaseOrder<'donation'>(order),
    establishment: transformEstablishment(order.establishment),
  };
};

export const transformReimbursementOrderDetails = <
  T extends TOrderCategorySlug,
>(
  order: IMenhirReimbursementOrderDetails<T>
): IReimbursementOrderDetails<T> => {
  const baseOrder: Omit<IOrder<T>, 'movements' | 'files'> = {
    id: order.id,
    isClosed: order.is_closed,
    product: CatalogDTO.transformProduct(order.product),
    status: order.status,
    amount: order.amount,
    reference: order.reference,
  };

  return {
    ...baseOrder,
    vatAmount: order.vat_amount,
    files: order.files.map((f) => ({
      id: f.id,
      slug: f.slug,
      url: f.url,
      name: f.name,
    })),
    iban: order.bank_account.number,
    statusDetails: order.status_details
      ? {
          date: order.status_details.at,
          reason: order.status_details.reason,
          updatedBy: order.status_details.by,
        }
      : undefined,
    receiptDate: order.purchased_at,
    transportationCard: order.transportation_card
      ? {
          number: order.transportation_card.number,
          expirationDate: order.transportation_card.expires_at,
        }
      : undefined,
    reimbursableItem: order.reimbursable_item
      ? {
          id: order.reimbursable_item.id,
          name: order.reimbursable_item.name,
          provider: order.reimbursable_item.provider,
          type: order.reimbursable_item.type,
        }
      : undefined,
    insertedAt: order.inserted_at,
    budgetEmployee: order.budget_employee,
    description: order.description,
    customFields: order.custom_fields?.map((field) => ({
      id: field.id,
      label: field.label,
      inputType: field.input_type,
      value: field.value,
    })),
    supportFiles: order.support_files?.map((f) => ({
      id: f.id,
      slug: f.slug,
      url: f.url,
      name: f.name,
    })),
  } as IReimbursementOrderDetails<T>;
};
